import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ResponsivePage from "../../common/core/ResponsivePage";
import config from "../../../config";
import { Task, useEntities } from "@emberly/zenith-client";
import { OPERATORS_QUERY } from "../../../common/queries";
import { useStation } from "../../../providers/StationProvider";
import { ToggleSurface } from "../../common/core/Surface";
import { Avatar, Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, Grid, IconButton, List, ListItem, ListItemAvatar, ListItemText, Menu, MenuItem, Skeleton, Stack, TextField, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { NavLink } from "react-router-dom";
import { MoreVert as MoreIcon } from "@mui/icons-material";
import { useNotification } from "../../../providers/NotificationProvider";
import ResponsiveDialog from "../../common/core/ResponsiveDialog";
import { MakeActivity } from "../../../common/activity";
import { ActivityEnums } from "../../../common/constants";


const useStyles = makeStyles()(
  (theme) => ({
    description: {
      marginBottom: theme.spacing(2),
      width: "100%"
    },
    link: {
      color: theme.palette.info.main
    },
    list: {
      width: "100%"
    }
  })
);

export default function OperatorsPage() {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const { entities: operators, loading, updateEntity } = useEntities("Employee", OPERATORS_QUERY);
  const { station } = useStation();
  const dispatcherOnDuty = useMemo(() => operators.find(t => t.id === station?.dispatcherOnDuty?.id), [station, operators]);

  return (
    <ResponsivePage
      title={t("operators:title")}
      maxWidth={config.pageSize.small}
    >

      <ToggleSurface
        title={t("operators:onDuty:title")}
      >

        <Grid item xs={12}>
          {!!dispatcherOnDuty ? (
            <OperatorItem operator={dispatcherOnDuty} t={t} classes={classes} />
          ) : (
            <EmptyOperatorItem loading={!station || loading} />
          )}
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary" className={classes.description}>
            {t("operators:onDuty:description")} <a to="/station/users" className={classes.link} href="https://help.getassist.app/nb/articles/12510642" target="_blank">{t("operators:onDuty:link")}</a>
          </Typography>
        </Grid>

      </ToggleSurface>

      <ToggleSurface
        title={
          <>
            <Typography variant="h5">{t("operators:list:title")}</Typography>
            <Typography variant="body2" color="textSecondary" className={classes.description}>
              {t("operators:list:description")} <NavLink to="/station/users" className={classes.link}>{t("operators:list:link")}</NavLink>
            </Typography>
          </>
        }
      >

        <Grid item xs={12}>
          {
            loading ? (
              <Box justifyContent="center" alignItems="center" sx={{ width: "100%", height: "192px", display: "flex" }}>
                <CircularProgress variant="indeterminate" />
              </Box>
            ) : (
              <List className={classes.list}>
                {operators.map((operator, key) => <OperatorItem operator={operator} key={key} t={t} classes={classes}  menu divider updateEntity={updateEntity}  />)}
              </List>
            )
          }
        </Grid>

      </ToggleSurface>


    </ResponsivePage >
  );
}


function OperatorItem(props) {
  const { operator, t, menu, divider, updateEntity } = props;
  const { station, updateStation, logEvent, pushToDispatch, permissions } = useStation();
  const { snackbar } = useNotification();

  const [anchorEl, setAnchorEl] = useState(null);
  const [editAction, setEditAction] = useState(null);

  const onClose = useCallback(() => {
    setAnchorEl(null);
  }, [])

  const onOpen = useCallback((ev) => {
    setAnchorEl(ev.currentTarget);
  }, [])

  const setOnDuty = useCallback(() => {
    updateStation({
      dispatcherOnDuty: { id: operator.id, name: operator.name }
    }).then(() => {
      snackbar(t("operators:notifications:setOnDuty"));
      // TODO send notification to new leader ?
      // TODO activity log
      pushToDispatch({
        title: t("notification:dispatcherOnDutyNotificationTitle"),
        message: `${t("notification:dispatcherOnDutyNotificationDescription")}`,
      });

      logEvent(MakeActivity(ActivityEnums.Category.Other, ActivityEnums.Type.DispatcherOnDuty, operator.id, "", `${t("operators:item:log")} ${operator.name}`, { id: operator.id, name: operator.name }));
    });

    onClose();
  }, [onClose, updateStation, operator, snackbar, t, logEvent, pushToDispatch]);

  const editInfo = useCallback(async () => {
    onClose();
    try {
      const t = new Task();
      setEditAction(t);
      await t.wait();
    } catch (err) {
      console.log(err)
    } finally {
      setEditAction(null);
    }
  }, [onClose]);

  return (
    <ListItem
      alignItems="flex-start"
      divider={divider}
      secondaryAction={
        menu ? (
          <IconButton onClick={onOpen}>
            <MoreIcon />
          </IconButton>
        ) : null
      }
    >
      <ListItemAvatar>
        <Avatar alt={operator.name} src={operator.picture} />
      </ListItemAvatar>
      <ListItemText
        primary={<Typography variant="subtitle1">{operator.name}</Typography>}
        secondary={<Typography variant="body2" color="textSecondary">{t("operators:item:phone")}: {operator.phoneNumber}</Typography>}
      />
      {
        menu ? (
          <Menu
            id="operator-menu"
            anchorEl={anchorEl}
            open={!!anchorEl}
            onClose={onClose}
          >
            <MenuItem onClick={setOnDuty} disabled={station?.dispatcherOnDuty?.id === operator?.id || !permissions.canSetDispatcherOnDuty}>{t("operators:item:setOnDuty")}</MenuItem>
            <MenuItem onClick={editInfo}>{t("operators:item:edit")}</MenuItem>
          </Menu>
        ) : null
      }
      {
        !!editAction ? (
          <EditOperatorDialog action={editAction} operator={operator} updateEntity={updateEntity} />
        ) : null
      }
    </ListItem>
  );
}


function EmptyOperatorItem(props) {
  const { t } = useTranslation();
  const { loading } = props;

  return (
    <ListItem
      alignItems="flex-start"
    >
      <ListItemAvatar>
        <Avatar alt={"empty"} />
      </ListItemAvatar>
      <ListItemText
        primary={<Typography variant="subtitle1">{loading ? <Skeleton variant="text" width={120} /> : t("operators:item:emptyName")}</Typography>}
        secondary={<Typography variant="body2" color="textSecondary">{loading ? <Skeleton variant="text" width={170} /> : t("operators:item:emptyDescription")}</Typography>}
      />
    </ListItem>
  )
}


function EditOperatorDialog(props) {
  const { action, operator, updateEntity } = props;
  const { t } = useTranslation();

  const [phoneNumber, setPhoneNumber] = useState(operator?.phoneNumber || "");
  const [working, setWorking] = useState(false);

  const hasChanges = !!operator && (
    phoneNumber !== operator?.phoneNumber
  );

  const onSave = async () => {
    try {
      setWorking(true);
      await updateEntity({ id: operator.id, phoneNumber }, ["phoneNumber"]);
      action.complete();
    } catch (err) {
      console.log(err);
    } finally {
      setWorking(false);
    }
  };

  return (
    <ResponsiveDialog
      open
      fullWidth
      maxWidth="xs"
      onClose={() => action.throw("cancelled")}
    >
      <DialogTitle>{t("operators:editDialog:title")} {operator.name}</DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body1">{t("operators:editDialog:description")}</Typography>

          <TextField
            label={t("common:phoneNumber")}
            variant="filled"
            value={phoneNumber}
            onChange={ev => setPhoneNumber(ev.target.value)}
            fullWidth
            size="small"
          />

        </Stack>

      </DialogContent>

      <DialogActions>
        <Button onClick={() => action.throw("cancelled")}>
          {t("common:cancel")}
        </Button>
        <Button onClick={onSave} variant="contained" color="primary" disabled={!hasChanges || working} loading={working}>
          {t("operators:editDialog:confirm")}
        </Button>
      </DialogActions>

    </ResponsiveDialog>
  )
}